import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { GraduationCap, BookOpen, Eye, Search, Settings2 } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useAuth } from '@/auth/context/AuthContext'
import { useConfiguracionesCEMC } from '../hooks/useCEMC'

export function DashboardCEMCPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const isTonalAdmin = user?.is_superuser || user?.groups?.includes('admin')

  const { data, isLoading, error } = useConfiguracionesCEMC({ page: 1, search: search || undefined })

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
          <GraduationCap className="mx-auto h-12 w-12 text-destructive mb-4" />
          <h3 className="text-lg font-semibold text-destructive mb-2">Error al cargar CEMC</h3>
          <p className="text-sm text-muted-foreground">No se pudieron cargar los reportes.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
          <GraduationCap className="h-7 w-7 text-violet-600" />
          CEMC - Educación Médica Continua
        </h1>
        {isTonalAdmin && (
          <Button
            onClick={() => navigate('/cemc-educacion/configuracion')}
            className="bg-violet-700 hover:bg-violet-800 text-white"
          >
            <Settings2 className="mr-2 h-4 w-4" />
            Configuración
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 max-w-sm">
        <Search className="h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Buscar reporte..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => <Skeleton key={i} className="h-40 rounded-xl" />)}
        </div>
      ) : !data?.results?.length ? (
        <div className="text-center py-16 text-muted-foreground">
          <GraduationCap className="mx-auto h-12 w-12 mb-4 opacity-30" />
          <p>No hay reportes CEMC disponibles</p>
        </div>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {data.results.map((config) => (
            <Card key={config.id} className="hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => navigate(`/cemc-educacion/reporte/${config.id}`)}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="rounded-lg bg-violet-100 p-2">
                    <GraduationCap className="h-5 w-5 text-violet-700" />
                  </div>
                  <Badge variant={config.activo ? 'default' : 'secondary'} className="text-xs">
                    {config.activo ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
                <CardTitle className="text-base mt-2">{config.titulo_reporte}</CardTitle>
                <CardDescription className="text-xs">{config.grupo_nombre}</CardDescription>
              </CardHeader>
              <CardContent className="pt-0">
                <div className="flex items-center justify-between text-xs text-muted-foreground mb-3">
                  <span className="flex items-center gap-1">
                    <BookOpen className="h-3 w-3" />
                    {config.cursos?.length || 0} cursos
                  </span>
                </div>
                <Button size="sm" className="w-full bg-violet-700 hover:bg-violet-800 text-white">
                  <Eye className="mr-2 h-3 w-3" />
                  Ver Reporte
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
